import { useCallback, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSessionJWT, useSessionCtx, useSessionStorage } from "utils";
import { verifyToken, logInfo, logError } from "api/accounts-customer";
import PlaidLinkWrapperOrMockLinkWrapper from "./PlaidLinkWrapperOrMockLinkWrapper";
import ErrorViewAndRedirect from "./ErrorViewAndRedirect";
import { storeAllowedRedirectPatterns } from "./allowedRedirectPatternsStorage";
import { FullPageSpinner } from "components/Spinner";

export const FLOW_STATE = {
  CANCEL: "plaid.cancel",
  PENDING: "plaid.pending",
  COMPLETE: "plaid.complete",
  ERROR: "plaid.error",
};

/**
 * @name PlaidMain
 * @description This page is meant to be the launching area for handling
 * interactions with Plaid. As part of Plaid's requirements we have to expect
 * that every interaction with Plaid will include a fullpage redirect OAuth flow
 * with a 3rd party banking site (even though most interactions with OAuth will
 * likely just be through a popup).
 * To cover edge cases with this scenario, we first redirect the user out of
 * the parent site (wether it's a Publisher webpage that is using the
 * widget script within an iframe, or if using something like CapacitorJS in a
 * native application).
 * As such, this page is never meant to be within an iFrame.
 * First thing this page will do is see if we are in a new context where we do not
 * have access to the user info in sessionStorage.  If that's the case, we validate
 * the state jwt we have (from a search parameter), then store it in sessionStorage
 * so it can be accessed later if we return from an OAuth flow.
 * It's entirely likely, that the user will complete the full Plaid interaction on
 * this page (the PlaidLinkWrapper and ErrorViewAndRedirect components take care
 * of the bulk of what is needed and redirect the user back to their origination
 * when done).
 * If the user does have to go through the fullpage OAuth redirect, then they
 * will return to a different page (/ui/plaid/success, OAuthSuccess component)
 * to complete the interaction.
*/
const PlaidMain = () => {
  const { sessionJWT, sessionData, setSessionJWTFromObject } = useSessionJWT();
  const { sessionCtx, setSessionCtxFromObject } = useSessionCtx();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const [state] = useState(searchParams.get("state"));
  const [plaidLinkToken] = useSessionStorage("tilia.ac.plink", searchParams.get("plink")); // we need to store the plaid link in storage in case we have to redirect out to an oauth flow
  const [error, setError] = useState(null);
  const [isMissingUserData, setIsMissingUserData] = useState(
    !sessionJWT || !sessionData || !sessionCtx
  ); // we should have all of these, if not set from state value
  const callToLoadStateJWTHasBeenFired = useRef(false);

  const validateJWTAndStoreState = useCallback(async () => {
    logInfo(
      {},
      {
        message:
          "[ACWEB] PlaidMain page: Browser is missing state.  We will validate the JWT and restore if valid.",
      }
    ); // don't have a debug function or sessionCtx here, just gonna logInfo
    try {
      const verifyResponse = await verifyToken(state);
      const session = verifyResponse.data;
      if (session.sessionCtx && session.sessionJWT) {
        setSessionCtxFromObject(session.sessionCtx);
        setSessionJWTFromObject(session.sessionJWT);
        // Cache the signed allowed redirect patterns so the redirect handlers can
        // enforce them after the fullpage OAuth bounce (SettingsProvider is not
        // populated on this route; the verified token is the trustworthy source).
        storeAllowedRedirectPatterns(session.allowedDomainRedirectPatterns);
        setIsMissingUserData(false);
      }
      setIsMissingUserData(false);
    } catch (err) {
      // show error
      logError(sessionJWT, { message: err.message, flowLoc: "plaid.redirect" });
      setError(err);
    }
  }, [setIsMissingUserData, setSessionCtxFromObject, setSessionJWTFromObject, state, sessionJWT]);

  /**
   * useEffect to handle loading the JWT from the state param
   * We may already have the JWT, but it's possible that it's
   * cached from an older run, so to be safe, let's pull it from
   * state each time.
   */
  useEffect(() => {
    const runValidateJWTAndStoreState = async () => {
      await validateJWTAndStoreState();
    };

    if (!callToLoadStateJWTHasBeenFired.current) {
      callToLoadStateJWTHasBeenFired.current = true;
      runValidateJWTAndStoreState();
    }
  }, [callToLoadStateJWTHasBeenFired, validateJWTAndStoreState]);

  let content = <FullPageSpinner />;

  // TODO: Need to show loading indicator if we are missing required data before showing PlaidLinkWrapper

  if (error) {
    content = <ErrorViewAndRedirect error={error} />;
  } else if (!isMissingUserData) {
    content = <PlaidLinkWrapperOrMockLinkWrapper plaidLinkToken={plaidLinkToken} />
  }

  return <span data-testid="plaid-main">{content}</span>;
};

export default PlaidMain;
